// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type {
  Attributes,
  QdsFocusEventDetail,
  QdsInputEventDetail,
} from '../../helpers'
import {
  inheritAriaAttributes,
  invariant,
  pickFocusEventAttributes,
  pickInputEventAttributes,
} from '../../helpers'
import type { EnterKeyHint, InputMode, Value } from '../controls'
import { CUSTOM_ERROR_FLAGS, NO_ERROR_FLAGS, VALID_STATE } from '../controls'
import type { Size } from '../shared'

export type TextAreaAutoComplete = 'off' | 'on'
// TODO: `hard` value isn't supported by Firefox or Safari
export type Wrap = 'off' | 'soft'

/**
 * The `<qds-textarea>` element represents a multi-line plain-text editing
 * control, useful when you want to allow users to enter a sizeable amount of
 * free-form text, for example a comment on a review or feedback form.
 *
 * @see https://quartz.se.com/build/components/textarea
 */
@Component({
  tag: 'qds-textarea',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'textarea.css',
})
export class TextArea implements ComponentInterface {
  /**
   * The textarea's size.
   *
   * > **_NOTE:_** The native [`size` HTML attribute][] is not supported. CSS
   * `width` should be used instead if this functionality is needed.
   *
   * [`size` HTML attribute]: https://developer.mozilla.org/docs/Web/HTML/Attributes/size
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Indicates whether the value of the textarea can be automatically completed
   * by the browser. Possible values:
   *
   * `off`: The user must explicitly enter a value into this field for every
   * use, or the document provides its own auto-completion method; the browser
   * does not automatically complete the entry.
   * `on`: The browser can automatically complete the value based on values
   * that the user has entered during previous uses.
   *
   * If the `autocomplete` attribute is not specified on a `<qds-textarea>`
   * element, then the browser uses the `autocomplete` attribute value of the
   * `<qds-textarea>` element's form owner. The form owner is either the
   * [`<form>`][] element that this `<qds-textarea>` element is a descendant of
   * or the form element whose `id` is specified by the `form` attribute of the
   * `qds-textarea` element. For more information, see the [`autocomplete`]
   * attribute in [`<form>`][].
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   * [`autocomplete`]: https://developer.mozilla.org/docs/Web/HTML/Element/form#autocomplete
   *
   * @webnative
   */
  @Prop({ attribute: 'autocomplete' })
  public readonly autoComplete?: TextAreaAutoComplete

  /**
   * The visible width of the text control, in average character widths. If it
   * is specified, it must be a positive integer. If it is not specified, the
   * default value is `20`.
   *
   * @webnative
   */
  @Prop() public readonly cols?: number

  /**
   * Prevents the textarea from being interacted with: it cannot be pressed or
   * focused.
   *
   * @webnative
   */
  @Prop() public disabled?: boolean

  /**
   * What action label (or icon) to present for the enter key on virtual
   * keyboards. Possible values:
   *
   * - `"done"`: Typically meaning there is nothing more to input and the input
   * method editor (IME) will be closed.
   * - `"enter"`:	Typically inserting a new line.
   * - `"go"`: 	Typically meaning to take the user to the target of the text
   * they typed.
   * - `"next"`: 	Typically taking the user to the next field that will accept
   * text.
   * - `"previous"`: Typically taking the user to the previous field that will
   * accept text.
   * - `"search"`: Typically taking the user to the results of searching for
   * the text they have typed.
   * - `"send"`: Typically delivering the text to its target.
   *
   * @webnative
   */
  // FIXME: Can't use `attribute` option here to name this `enterKeyHint` instead.
  @Prop() public readonly enterkeyhint?: EnterKeyHint

  /**
   * The [`<form>`][] element to associate the input with (its form owner).
   *
   * The value of this attribute must be the id of a `<form>` in the same
   * document. If this attribute is not set, the `<qds-textarea>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This attribute lets you associate `<qds-textarea>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   * an ancestor `<form>` element.
   *
   * [`<form>`]: https://developer.mozilla.org/docs/Web/HTML/Element/form
   *
   * @readonly
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Provide a hint to browsers as to the type of virtual keyboard
   * configuration to use when editing this element or its contents.
   *
   * @webnative
   */
  // FIXME: Can't use `attribute` option here to name this `inputMode` instead.
  @Prop() public readonly inputmode?: InputMode

  /**
   * Returns a list of the [`<label>`][] elements associated with the
   * `qds-textarea` element.
   *
   * [`<label>`]: https://developer.mozilla.org/docs/Web/HTML/Element/label
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly labels: ElementInternals['labels'] =
    {} as ElementInternals['labels']

  /**
   * The maximum number of characters (as UTF-16 code units) the user can enter
   * into the textarea. This must be an non-negative integer value. If no
   * `maxlength` is specified, or an invalid value is specified, the textarea
   * has no maximum length.
   *
   * The textarea will fail [constraint validation][] if the length of the text
   * entered into the field is greater than `maxlength` UTF-16 code units long.
   * By default, browsers prevent users from entering more characters than
   * allowed by the `maxlength` attribute. See [Client-side validation][] for
   * more information.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   *
   * @webnative
   */
  @Prop({ attribute: 'maxlength' }) public readonly maxLength?: number

  /**
   * The minimum number of characters (as UTF-16 code units) the user can enter
   * into the textarea. This must be an non-negative integer value smaller than
   * or equal to the value specified by `maxlength`. If no `minlength` is
   * specified, or an invalid value is specified, the textarea has no minimum
   * length.
   *
   * The textarea will fail [constraint validation][] if the length of the text
   * entered into the field is fewer than `minlength` UTF-16 code units long,
   * preventing form submission. See [Client-side validation][] for more
   * information.
   *
   * [constraint validation]: https://developer.mozilla.org/docs/Web/Guide/HTML/Constraint_validation
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   *
   * @webnative
   */
  @Prop({ attribute: 'minlength' }) public readonly minLength?: number

  /**
   * The name of the textarea, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Text that appears in the textarea when it has no value set.
   *
   * @webnative
   */
  @Prop() public readonly placeholder?: string

  /**
   * A value must be specified for the textarea before the owning form can be
   * submitted.
   *
   * See [Client-side validation][] and the [HTML attribute: `required`][] for
   * more information.
   *
   * [Client-side validation]: https://developer.mozilla.org/docs/Web/HTML/Element/input#client-side_validation
   * [HTML attribute: `required`]: https://developer.mozilla.org/docs/Web/HTML/Attributes/required
   *
   * @webnative
   */
  @Prop() public readonly required?: boolean

  /**
   * The number of visible text lines for the control. If it is specified, it
   * must be a positive integer. If it is not specified, the default value is
   * `2`.
   *
   * @webnative
   */
  @Prop() public readonly rows?: number

  /**
   * Get or set the selection direction of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // FIXME: Use `HTMLTextAreaElement['selectionDirection']` instead.
  // eslint-disable-next-line unicorn/no-null
  public selectionDirection: HTMLInputElement['selectionDirection'] = null

  /**
   * Get or set the ending position or offset of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // FIXME: Use `HTMLTextAreaElement['selectionEnd']` instead.
  // eslint-disable-next-line unicorn/no-null
  public selectionEnd: HTMLInputElement['selectionEnd'] = null

  /**
   * Get or set the starting position or offset of a text selection.
   *
   * > **_NOTE:_** This property has no effect when used as an attribute.
   *
   * @webnative
   */
  @Prop({ mutable: true })
  // FIXME: Use `HTMLTextAreaElement['selectionStart']` instead.
  // eslint-disable-next-line unicorn/no-null
  public selectionStart: HTMLInputElement['selectionStart'] = null

  /**
   * The error message that would be shown to the user if the `<qds-select>`
   * was to be checked for validity.
   *
   * @readonly
   * @webnative
   */
  @Prop()
  public readonly validationMessage: ElementInternals['validationMessage'] = ''

  /**
   * The [`ValidityState`][] object for this `<qds-select>`.
   *
   * [`ValidityState`]: https://developer.mozilla.org/docs/Web/API/ValidityState
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly validity: ElementInternals['validity'] = VALID_STATE

  /**
   * The value of the textarea.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public value?: Value

  /**
   * True if `<qds-textarea>` will be validated when the form is submitted;
   * false otherwise.
   *
   * @readonly
   * @webnative
   */
  @Prop() public readonly willValidate: ElementInternals['willValidate'] = false

  /**
   * Indicates how the control should wrap the value for form submission.
   * Possible values are:
   *
   * - `hard`: The browser automatically inserts line breaks (`CR+LF`) so that
   * each line is no longer than the width of the control; the `cols` attribute
   * must be specified for this to take effect
   * - `soft`: The browser ensures that all line breaks in the entered value
   * are a `CR+LF` pair, but no additional line breaks are added to the value.
   * - `off`: Like `soft` but changes appearance to `white-space: pre` so line
   * segments exceeding `cols` are not wrapped and the `<qds-textarea>` becomes
   * horizontally scrollable.
   *
   * If this attribute is not specified, `soft` is its default value.
   *
   * @webnative
   */
  @Prop() public readonly wrap?: Wrap

  @AttachInternals() private readonly internals!: ElementInternals

  /**
   * Emitted when the textarea loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when an alteration to the textarea's value is committed by the
   * user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the textarea gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the textarea's value changes.
   */
  @Event({ eventName: 'qdsInput', cancelable: false })
  private readonly inputEmitter!: EventEmitter<QdsInputEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  #textarea?: HTMLTextAreaElement

  get #computedDisabled(): boolean {
    return (
      (this.host.matches(':disabled') || (this.disabled ?? false)) &&
      this.host.getAttribute('disabled') !== 'false'
    )
  }

  get #computedValue(): Parameters<ElementInternals['setFormValue']>[0] {
    if (typeof this.value === 'number') return this.value.toString()
    // eslint-disable-next-line unicorn/no-null
    return this.value === undefined ? null : this.value
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) event.stopImmediatePropagation()
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    if (this.#computedDisabled)
      // eslint-disable-next-line unicorn/no-null
      this.internals.setFormValue(null)
    else this.internals.setFormValue(this.#computedValue)
  }

  @Watch('selectionDirection')
  protected selectionDirectionChanged(): void {
    invariant(this.#textarea)

    // @ts-expect-error: `null` is a valid value according to the HTML spec.
    this.#textarea.selectionDirection = this.selectionDirection
  }

  @Watch('selectionEnd')
  protected selectionEndChanged(): void {
    invariant(this.#textarea)

    // @ts-expect-error: `null` is a valid value according to the HTML spec.
    this.#textarea.selectionEnd = this.selectionEnd
  }

  @Watch('selectionStart')
  protected selectionStartChanged(): void {
    invariant(this.#textarea)

    // @ts-expect-error: `null` is a valid value according to the HTML spec.
    this.#textarea.selectionStart = this.selectionStart
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('maxLength')
  @Watch('minLength')
  @Watch('required')
  @Watch('value')
  protected validationChanged(): void {
    invariant(this.#textarea)

    if (this.maxLength !== undefined) this.#textarea.maxLength = this.maxLength
    if (this.minLength !== undefined) this.#textarea.minLength = this.minLength
    this.#textarea.required = this.required ?? false
    // eslint-disable-next-line unicorn/no-null
    if (this.value != null) this.#textarea.value = this.value.toString()

    const { valid } = this.#textarea.validity
    if (valid)
      this.internals.setValidity(NO_ERROR_FLAGS, undefined, this.#textarea)
    else
      this.internals.setValidity(
        this.#textarea.validity,
        this.#textarea.validationMessage,
        this.#textarea,
      )
  }

  @Watch('value')
  protected valueChanged(): void {
    if (!this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)
  }

  public componentWillLoad(): void {
    this.#defineGetter('form', () => this.internals.form)
    this.#defineGetter('willValidate', () => this.internals.willValidate)
    this.#defineGetter('validity', () => this.internals.validity)
    this.#defineGetter(
      'validationMessage',
      () => this.internals.validationMessage,
    )
    this.#defineGetter('labels', () => this.internals.labels)

    this.#defineGetter('selectionDirection', this.#getSelectionDirection)
    this.#defineGetter('selectionStart', this.#getSelectionStart)
    this.#defineGetter('selectionEnd', this.#getSelectionEnd)

    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public componentDidLoad(): void {
    invariant(this.#textarea)

    this.validationChanged()
    this.valueChanged()
  }

  public render() {
    return (
      <Host>
        <textarea
          autoCapitalize={this.host.autocapitalize}
          autoComplete={this.autoComplete}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={this.host.autofocus}
          class="qds-textarea"
          cols={this.cols}
          data-size={this.size ?? 'standard'}
          disabled={this.disabled}
          enterKeyHint={this.enterkeyhint}
          inputMode={this.inputmode}
          maxLength={this.maxLength}
          minLength={this.minLength}
          onBlur={this.#onBlur}
          onChange={this.#onChange}
          onFocus={this.#onFocus}
          onInput={this.#onInput}
          placeholder={this.placeholder}
          ref={this.#ref}
          rows={this.rows}
          spellcheck={this.host.spellcheck}
          tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
          wrap={this.wrap}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.#inheritedAttributes}
        />
        <slot onSlotchange={this.#onSlotchange} />
      </Host>
    )
  }

  public checkValidity: ElementInternals['checkValidity'] = () =>
    this.internals.checkValidity()

  public reportValidity: ElementInternals['reportValidity'] = () =>
    this.internals.reportValidity()

  public setCustomValidity: HTMLTextAreaElement['setCustomValidity'] = (
    error,
  ) => {
    invariant(this.#textarea)

    this.#textarea.setCustomValidity(error)
    if (error)
      this.internals.setValidity(CUSTOM_ERROR_FLAGS, error, this.#textarea)
    else this.internals.setValidity(NO_ERROR_FLAGS)
  }

  public select: HTMLTextAreaElement['select'] = () => {
    invariant(this.#textarea)

    this.#textarea.select()
  }

  public setRangeText = (
    replacement: string,
    start: number,
    end: number,
    selectionMode?: SelectionMode,
  ): void => {
    invariant(this.#textarea)

    this.#textarea.setRangeText(replacement, start, end, selectionMode)

    if (this.value !== this.#textarea.value) {
      this.value = this.#textarea.value
      this.inputEmitter.emit()
      this.changeEmitter.emit()
    }
  }

  public setSelectionRange: HTMLTextAreaElement['setSelectionRange'] = (
    start,
    end,
    direction,
  ) => {
    invariant(this.#textarea)

    this.#textarea.setSelectionRange(start, end, direction)
  }

  readonly #getSelectionDirection: () => HTMLTextAreaElement['selectionDirection'] =
    () => {
      invariant(this.#textarea)

      return this.#textarea.selectionDirection
    }

  readonly #getSelectionStart: () => HTMLTextAreaElement['selectionStart'] =
    () => {
      invariant(this.#textarea)

      return this.#textarea.selectionStart
    }

  readonly #getSelectionEnd: () => HTMLTextAreaElement['selectionEnd'] = () => {
    invariant(this.#textarea)

    return this.#textarea.selectionEnd
  }

  readonly #ref = (textarea?: HTMLTextAreaElement): void => {
    this.#textarea = textarea
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onChange = (): void => {
    this.changeEmitter.emit()
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onInput = (event: InputEvent): void => {
    invariant(this.#textarea)

    this.value = this.#textarea.value
    this.inputEmitter.emit(pickInputEventAttributes(event))
  }

  readonly #onSlotchange = (): void => {
    invariant(this.#textarea)
    this.#textarea.innerHTML = this.host.innerHTML
  }

  #defineGetter(p: PropertyKey, get: () => unknown): void {
    Object.defineProperty(this.host, p, { enumerable: true, get })
  }
}
